// Submit handler for the lease/buy enquiry form.
// Wires up Turnstile, validates basics, POSTs to the worker's
// /lease-buy-enquiry endpoint, and toggles inline success/error state.

import { onReady } from "#public/utils/on-ready.js";

const FORM_SELECTOR = "form[data-lease-buy-form]";

const TURNSTILE_WIDGETS = new Map();

const whenTurnstileReady = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(null);
    if (window.turnstile) return resolve(window.turnstile);
    const start = Date.now();
    const poll = () => {
      if (window.turnstile) return resolve(window.turnstile);
      if (Date.now() - start > 10000) return resolve(null);
      setTimeout(poll, 100);
    };
    poll();
  });

const renderTurnstile = async (placeholderId, sitekey) => {
  if (!placeholderId || !sitekey) return;
  const placeholder = document.getElementById(placeholderId);
  if (!placeholder) return;
  if (TURNSTILE_WIDGETS.has(placeholderId)) return;
  const turnstile = await whenTurnstileReady();
  if (!turnstile) return;
  try {
    const id = turnstile.render(`#${placeholderId}`, {
      sitekey,
      action: "lease-buy-enquiry",
      theme: "light",
    });
    if (id) TURNSTILE_WIDGETS.set(placeholderId, id);
  } catch (_e) {
    // No-op: render can throw if already mounted.
  }
};

const resetTurnstile = (placeholderId) => {
  if (typeof window === "undefined" || !window.turnstile) return;
  const widgetId = TURNSTILE_WIDGETS.get(placeholderId);
  if (!widgetId) return;
  try {
    window.turnstile.reset(widgetId);
  } catch (_e) {
    // No-op
  }
};

const getValue = (form, name) => {
  const el = form.querySelector(`[name="${name}"]`);
  return el && typeof el.value === "string" ? el.value.trim() : "";
};

const getRadioValue = (form, name) => {
  const checked = form.querySelector(`input[name="${name}"]:checked`);
  return checked ? checked.value : "";
};

const getTurnstileToken = (form) => {
  const el = form.querySelector("[name='cf-turnstile-response']");
  return el && el.value ? el.value : "";
};

const showMessage = (form, kind, text) => {
  const msg = form.querySelector("[data-lease-buy-msg]");
  if (!msg) return;
  msg.hidden = false;
  msg.classList.remove("lb-form__msg--success", "lb-form__msg--error");
  msg.classList.add(`lb-form__msg--${kind}`);
  msg.textContent = text;
};

const clearMessage = (form) => {
  const msg = form.querySelector("[data-lease-buy-msg]");
  if (!msg) return;
  msg.hidden = true;
  msg.textContent = "";
};

const setSubmitting = (form, isSubmitting) => {
  const btn = form.querySelector("[data-lease-buy-submit]");
  if (!btn) return;
  btn.disabled = isSubmitting;
  btn.textContent = isSubmitting ? "Sending…" : "Send enquiry";
};

const buildPayload = (form) => ({
  turnstile_token: getTurnstileToken(form),
  website: getValue(form, "website"),
  page_source: form.dataset.pageSource || "buy",
  name: getValue(form, "name"),
  business_name: getValue(form, "business_name"),
  email: getValue(form, "email"),
  phone: getValue(form, "phone"),
  intent: getRadioValue(form, "intent") || "not-sure",
  timeline: getValue(form, "timeline"),
  budget: getValue(form, "budget"),
  message: getValue(form, "message"),
});

const validate = (payload) => {
  if (!payload.name) return "Please add your name.";
  if (!payload.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
    return "Please add a valid email address.";
  }
  if (!payload.message) {
    return "Please tell us a bit about your business so we can come back with something useful.";
  }
  return null;
};

const submitForm = async (form) => {
  clearMessage(form);

  const workerUrl = (form.dataset.workerUrl || "").trim();
  if (!workerUrl) {
    showMessage(
      form,
      "error",
      "Form isn't configured — please call us on 03331 126993.",
    );
    return;
  }

  const payload = buildPayload(form);
  const validationError = validate(payload);
  if (validationError) {
    showMessage(form, "error", validationError);
    return;
  }

  if (!payload.turnstile_token) {
    showMessage(
      form,
      "error",
      "Please wait for the security check to finish, then submit again.",
    );
    return;
  }

  setSubmitting(form, true);
  let ok = false;
  try {
    const response = await fetch(
      `${workerUrl.replace(/\/$/, "")}/lease-buy-enquiry`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      },
    );
    ok = response.ok;
  } catch (_err) {
    ok = false;
  }

  if (!ok) {
    const placeholder = form.querySelector("[data-lease-buy-turnstile]");
    if (placeholder && placeholder.id) resetTurnstile(placeholder.id);
    showMessage(
      form,
      "error",
      "Sorry, something went wrong sending your enquiry. Please try again or call us on 03331 126993.",
    );
    setSubmitting(form, false);
    return;
  }

  showMessage(
    form,
    "success",
    "Thanks — we've got it. One of the team will be back to you within one working day.",
  );
  form.reset();
  setSubmitting(form, false);
  const placeholder = form.querySelector("[data-lease-buy-turnstile]");
  if (placeholder && placeholder.id) resetTurnstile(placeholder.id);
};

onReady(() => {
  for (const form of document.querySelectorAll(FORM_SELECTOR)) {
    const placeholder = form.querySelector("[data-lease-buy-turnstile]");
    const sitekey = form.dataset.turnstileKey || "";
    if (placeholder && placeholder.id) {
      renderTurnstile(placeholder.id, sitekey);
    }

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      submitForm(form);
    });
  }
});
